import { t } from '../../../shared/config/translations'
import type {
	ClientRelease,
	ReleaseHistory,
} from '../../../shared/types/collection'
import {
	formatDate,
	getFormattedLastHeardFromHistoryAsString,
} from '../../utils/format'
import {
	listenedDialogTemplate,
	releaseInfoRowTemplate,
	releaseTracklistTemplate,
} from './releaseRendererTemplates'

export function renderHistoryList(
	history: ReleaseHistory | null,
	dateAddedRaw: string,
): string {
	const knownLastHeard = getFormattedLastHeardFromHistoryAsString(
		history?.historyDates ?? [],
	)
	const lastHeard =
		knownLastHeard ||
		((history?.unknownDateCount ?? 0) > 0
			? t('release.unknownDateSummary')
			: t('release.noRecord'))
	const hiddenUntil = formatHiddenUntil(history?.hiddenUntil)
	const hiddenUntilDisplay =
		history?.isHidden && !hiddenUntil
			? t('release.hideForever')
			: hiddenUntil
	const listenCount = history?.listenCount ?? t('release.noRecord')
	const dateAdded = dateAddedRaw ? formatDate(dateAddedRaw) : '–'

	return `
        ${releaseInfoRowTemplate(t('release.addedLabel').replace(':', ''), dateAdded)}
        ${releaseInfoRowTemplate(t('release.lastHeardLabel').replace(':', ''), lastHeard)}
        ${releaseInfoRowTemplate(t('release.countLabel').replace(':', ''), String(listenCount))}
        ${hiddenUntilDisplay ? releaseInfoRowTemplate(t('release.hiddenUntilLabel').replace(':', ''), hiddenUntilDisplay) : ''}
    `
}

export function renderReleaseInfoList(release: ClientRelease): string {
	const data = release.data
	const formats = Array.isArray(data.formats) ? data.formats : []
	const formatNames = new Set<string>()
	const formatDescriptions = new Set<string>()
	const formatTexts = new Set<string>()

	formats.forEach((format) => {
		if (format.name) {
			const capitalized =
				format.name[0].toUpperCase() + format.name.slice(1)
			formatNames.add(capitalized)
		}
		if (Array.isArray(format.descriptions)) {
			format.descriptions
				.filter(Boolean)
				.forEach((description) => formatDescriptions.add(description))
		}
		if (format.text) {
			formatTexts.add(format.text)
		}
	})

	const formatParts = [
		...Array.from(formatNames),
		...Array.from(formatDescriptions),
		...Array.from(formatTexts),
	]
	const dataFormats = formatParts.length ? formatParts.join(', ') : '–'
	const labels = formatReleaseLabels(data.labels)
	const released = data.released ? formatDate(data.released) : '–'
	const releasedWithCountry = data.country
		? `${released} (${data.country})`
		: released
	const communityRating = formatCommunityRating(
		data.community?.rating.average,
		data.community?.rating.count,
	)

	return `
        ${releaseInfoRowTemplate('Released', releasedWithCountry)}
        ${releaseInfoRowTemplate(t('release.format'), dataFormats)}
        ${releaseInfoRowTemplate(t('release.labelLabel').replace(':', ''), labels)}
        ${releaseInfoRowTemplate(t('release.genreLabel').replace(':', ''), data.genres?.length ? data.genres.join(', ') : '–')}
        ${releaseInfoRowTemplate(t('release.styleLabel').replace(':', ''), data.styles?.length ? data.styles.join(', ') : '–')}
		${communityRating ? releaseInfoRowTemplate(t('release.discogsRating'), communityRating) : ''}
        ${releaseInfoRowTemplate('Discogs ID', String(data.id ?? release.id), { copyValue: String(data.id ?? release.id), rowClassName: 'grouped' })}
        ${data.notes ? releaseInfoRowTemplate('Notes', data.notes, { stacked: true, valueClassName: 'f secondary caption' }) : ''}
    `
}

export function renderListenedDialog(hideDurationOptions: string): string {
	return listenedDialogTemplate(hideDurationOptions)
}

export function renderTracklist(data: ClientRelease['data']): string {
	return releaseTracklistTemplate(data)
}

function formatHiddenUntil(
	hiddenUntil: ReleaseHistory['hiddenUntil'],
): string | null {
	if (!hiddenUntil) return null
	const date = new Date(hiddenUntil)
	if (Number.isNaN(date.getTime())) return null
	const days = Math.max(
		0,
		Math.ceil((date.getTime() - Date.now()) / (24 * 60 * 60 * 1000)),
	)
	return `${formatDate(date)} (${days} ${days === 1 ? 'day' : 'days'})`
}

function formatCommunityRating(
	average?: number,
	count?: number,
): string | null {
	const valid =
		typeof average === 'number' &&
		Number.isFinite(average) &&
		typeof count === 'number' &&
		Number.isFinite(count) &&
		count > 0
	return valid
		? `${average.toFixed(2)} · ${count} ${count === 1 ? t('release.rating') : t('release.ratingsCount')}`
		: null
}

function formatReleaseLabels(labels: ClientRelease['data']['labels']): string {
	if (!Array.isArray(labels)) return '–'

	const labelNames = labels
		.map((label) => label.name?.trim())
		.filter((name): name is string => !!name)

	return labelNames.length ? labelNames.join(', ') : '–'
}
